/**
 * Ficha do ativo: resumo, dados, ficha técnica, telemetria e histórico, com o ativo em 3D — Nexora Grid (frontend, US-406, US-4603).
 *
 * Criado em: 16/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * v2.0 (Sprint 46, protótipo aprovado): cabeçalho com migalhas e as ações (Validar, Rejeitar…), o equipamento em 3D
 * (arraste para girar) e a última leitura à esquerda, e as abas à direita. Dados em grupos com validação na hora e
 * rodapé Salvar/Cancelar; o motivo da alteração e o da rejeição vêm num diálogo próprio (não mais campo solto). O
 * histórico é a linha do tempo das versões (nada se apaga). Mesmas chamadas de antes: `PUT /v1/ativos/{id}`,
 * `/validar`, `/rejeitar`, `/auditoria` e a `FichaForm` (corte do poço e curva da bomba dentro dela).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ErroApi } from "../api";
import { fmtValor, nomeMedida, unidadeMedida } from "../cco/medidas";
import { Abas, avisar, CabecalhoTela, Campo, DialogoMotivo, EstadoAlarme, EstadoVazio, ErroCarregar, Esqueleto, GrupoCampos, RodapeFormulario, Selo,
  useAvisoAoSair, useParametro } from "../componentes/Tela";
import { PreviaAtivo3D } from "../componentes/PreviaAtivo3D";
import { idadeRelativa } from "../componentes/regrasTela";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { CampoSetor } from "./CampoSetor";
import { FichaForm } from "./FichaForm";
import { MapaPosicao } from "./MapaPosicao";
import { estadoDoAtivo, leituraPrincipal } from "./regrasCadastro";
import { fmtData, ORIGENS, SITUACOES, TIPOS, type Ativo, type DadosAtivo } from "./tipos";
import { serie24h, useTelemetriaAtivos } from "./useTelemetriaAtivos";

type Evento = { instante: string; autor: string; tipo_autor: string; evento: string; versao: number;
  mudancas: Record<string, unknown>; motivo: string };
type Aba = "resumo" | "dados" | "ficha" | "telemetria" | "historico";

const EVENTOS: Record<string, string> = { criado: "Criado", alterado: "Dados alterados", validado: "Validado",
  rejeitado: "Rejeitado", ficha_alterada: "Ficha técnica alterada" };
const ABAS: { chave: Aba; texto: string }[] = [{ chave: "resumo", texto: "Resumo" }, { chave: "dados", texto: "Dados" },
  { chave: "ficha", texto: "Ficha técnica" }, { chave: "telemetria", texto: "Telemetria" }, { chave: "historico", texto: "Histórico" }];
const TOM: Record<string, "ok" | "atencao" | "ruim"> = { validado: "ok", pendente: "atencao", rejeitado: "ruim" };
const texto = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

/** Erros dos dados na hora (o backend confere de novo). */
export function errosDosDados(d: DadosAtivo): Partial<Record<keyof DadosAtivo, string>> {
  const e: Partial<Record<keyof DadosAtivo, string>> = {};
  if (!Number.isFinite(d.latitude) || d.latitude < -90 || d.latitude > 90) e.latitude = "Latitude entre -90 e 90.";
  if (!Number.isFinite(d.longitude) || d.longitude < -180 || d.longitude > 180) e.longitude = "Longitude entre -180 e 180.";
  if (d.cota_m !== null && (d.cota_m < -500 || d.cota_m > 9000)) e.cota_m = "Cota fora da faixa (-500 a 9.000 m).";
  if (d.device_id && !/^[A-Za-z0-9_.:-]{2,80}$/.test(d.device_id)) e.device_id = "Use letras, números, ponto, hífen ou sublinhado.";
  return e;
}

/** Gráfico de 24 h de uma medida, na escala (eixos com valores reais). */
function Grafico24h({ pontos, medida }: { pontos: [string, number][]; medida: string }) {
  if (pontos.length < 2) return <EstadoVazio titulo="Sem leituras nas últimas 24 horas" />;
  const L = 720, A = 220, m = 44, mb = 20;
  const t = pontos.map(([i]) => new Date(i).getTime()), v = pontos.map(([, x]) => x);
  const t0 = Math.min(...t), t1 = Math.max(...t), v0 = Math.min(...v), v1 = Math.max(...v), folga = (v1 - v0) * 0.1 || 1;
  const x = (ti: number) => m + ((ti - t0) / Math.max(1, t1 - t0)) * (L - m - 6), y = (vi: number) => 6 + (1 - (vi - (v0 - folga)) / (v1 - v0 + 2 * folga)) * (A - 12 - mb);
  const d = pontos.map((_, i) => `${i ? "L" : "M"}${x(t[i]).toFixed(1)},${y(v[i]).toFixed(1)}`).join("");
  const marcas = [0, 1, 2, 3].map((k) => v0 - folga + (k / 3) * (v1 - v0 + 2 * folga));
  return (
    <svg viewBox={`0 0 ${L} ${A}`} className="grafico-24h" role="img" aria-label={`${nomeMedida(medida)} nas últimas 24 horas`}>
      {marcas.map((mv) => <g key={mv}><line className="grade" x1={m} x2={L} y1={y(mv)} y2={y(mv)} />
        <text x={m - 6} y={y(mv) + 4} textAnchor="end">{fmtValor(medida, mv)}</text></g>)}
      {[t0, (t0 + t1) / 2, t1].map((ti, k) => <text key={k} x={x(ti)} y={A - 4} textAnchor={["start", "middle", "end"][k] as "start"}>
        {new Date(ti).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</text>)}
      <path className="area" d={`${d}L${x(t1)},${A - mb}L${x(t0)},${A - mb}Z`} /><path className="linha" d={d} />
    </svg>
  );
}

export function Detalhe() {
  const { id } = useParams();
  const { usuario, empresa } = useSessao();
  const [ativo, setAtivo] = useState<Ativo | null>(null);
  const [dados, setDados] = useState<DadosAtivo | null>(null);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aba, setAba] = useParametro("aba", "resumo");
  const [dialogo, setDialogo] = useState<null | "salvar" | "rejeitar">(null);
  const [salvando, setSalvando] = useState(false);
  const [serie, setSerie] = useState<{ medida: string; pontos: [string, number][] } | null>(null);
  const telemetria = useTelemetriaAtivos(empresa);
  const editavel = !!usuario && pode.editarCadastro(usuario);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const a = await api<Ativo>(`/v1/ativos/${id}`);
      setAtivo(a);
      setDados(a.dados);
      setEventos(await api<Evento[]>(`/v1/ativos/${id}/auditoria`).catch(() => []));
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : String(e));
    }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  const tel = ativo?.dados.device_id ? telemetria.get(ativo.dados.device_id) : undefined;
  const estado = ativo ? estadoDoAtivo(ativo, tel) : "semtel";
  const leitura = ativo ? leituraPrincipal(ativo.dados.tipo, tel) : null;
  useEffect(() => {
    if (aba !== "telemetria" || !empresa || !ativo?.dados.device_id || !leitura || serie?.medida === leitura.medida) return;
    serie24h(empresa, ativo.dados.device_id, leitura.medida).then((p) => setSerie({ medida: leitura.medida, pontos: p })).catch(() => setSerie({ medida: leitura.medida, pontos: [] }));
  }, [aba, empresa, ativo?.dados.device_id, leitura?.medida]);  // eslint-disable-line react-hooks/exhaustive-deps

  const mudou = !!ativo && !!dados && JSON.stringify(dados) !== JSON.stringify(ativo.dados);
  const erros = useMemo(() => (dados ? errosDosDados(dados) : {}), [dados]);
  useAvisoAoSair(mudou);

  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    setSalvando(true);
    try { await acao(); await carregar(); avisar(ok); setDialogo(null); } catch (e) {
      avisar(e instanceof ErroApi ? e.message : String(e), "erro");
    } finally { setSalvando(false); }
  };
  if (erro) return <section className="tl-pagina"><CabecalhoTela migalhas={[{ texto: "Cadastro", para: "/cadastro" }, { texto: "Ativo" }]} titulo="Ativo" voltar="/cadastro" />
    <ErroCarregar mensagem={erro} aoTentar={() => void carregar()} /></section>;
  if (!ativo || !dados || ativo.id !== id) return <section className="tl-pagina"><Esqueleto linhas={10} /></section>;
  if (!usuario || !pode.verEmpresa(usuario, ativo.empresa)) return <EstadoVazio titulo="Ativo não encontrado" />;
  const d = ativo.dados;
  const campo = (nome: keyof DadosAtivo, rotulo: string, tipo = "text", unidade?: string) => (
    <Campo id={`dado-${nome}`} rotulo={rotulo} erro={erros[nome]} unidade={unidade}>
      <input id={`dado-${nome}`} type={tipo} step="any" disabled={!editavel} value={dados[nome] === null ? "" : String(dados[nome])}
        aria-invalid={!!erros[nome]} onChange={(e) => setDados({ ...dados, [nome]: tipo === "number" ? (e.target.value === "" ? null : Number(e.target.value))
          : (nome === "device_id" && e.target.value === "" ? null : e.target.value) })} />
    </Campo>
  );

  return (
    <section className="tl-pagina ficha-ativo">
      <CabecalhoTela voltar="/cadastro" migalhas={[{ texto: "Cadastro", para: "/cadastro" }, { texto: "Ativos", para: "/cadastro" }, { texto: d.identificacao }]}
        titulo={<>{d.identificacao}{d.nome && <span className="nx-suave" style={{ fontWeight: 500 }}> · {d.nome}</span>}</>}
        selos={<><Selo tom={TOM[ativo.situacao]}>{SITUACOES[ativo.situacao]}</Selo><EstadoAlarme estado={estado} /></>}
        acoes={editavel && <>
          {ativo.situacao !== "validado" && <button type="button" className="nx-botao nx-botao-principal" disabled={salvando}
            onClick={() => void executar(() => api(`/v1/ativos/${id}/validar`, { metodo: "POST", corpo: {} }), `${d.identificacao} validado`)}>Validar</button>}
          <button type="button" className="nx-botao" onClick={() => setAba("dados")}>Editar</button>
          {ativo.situacao !== "rejeitado" && <button type="button" className="tl-fantasma" onClick={() => setDialogo("rejeitar")}>Rejeitar…</button>}
        </>} />
      {ativo.motivo_situacao && <p className="nx-suave" style={{ margin: 0 }}>Motivo da situação: {ativo.motivo_situacao}</p>}
      <div className="ficha-grade">
        <div className="ficha-lado">
          <div className="tl-bloco"><PreviaAtivo3D tipo={d.tipo} identificacao={d.identificacao} estado={estado} rotulo={TIPOS[d.tipo] ?? d.tipo} altura={320} arrastar /></div>
          {leitura && (
            <div className="medida-ativo"><small>{nomeMedida(leitura.medida)}{tel?.alarme ? ` · ${tel.alarme}` : ""}</small>
              <span className="nx-suave">{idadeRelativa(leitura.timestamp)}</span>
              <strong>{fmtValor(leitura.medida, leitura.valor)}<em>{unidadeMedida(leitura.medida)}</em></strong></div>
          )}
        </div>
        <section className="tl-bloco" aria-label={`Ficha de ${d.identificacao}`}>
          <Abas abas={ABAS} ativa={aba as Aba} aoMudar={setAba} rotulo="Partes da ficha" />
          {aba === "resumo" && (
            <div className="tl-painel-aba">
              <dl className="tl-kv" style={{ fontSize: 14 }}>
                <dt>Identificação</dt><dd>{d.identificacao}</dd><dt>Nome</dt><dd>{d.nome || "—"}</dd><dt>Tipo</dt><dd>{TIPOS[d.tipo] ?? d.tipo}</dd>
                <dt>Setor</dt><dd>{d.setor || "—"}</dd><dt>Unidade</dt><dd>{d.unidade || "—"}</dd><dt>Dispositivo</dt><dd>{d.device_id || "—"}</dd>
                <dt>Estado agora</dt><dd><EstadoAlarme estado={estado} />{tel?.alarme ? ` · ${tel.alarme}` : ""}</dd>
                <dt>Cota</dt><dd>{d.cota_m === null ? "—" : `${d.cota_m.toLocaleString("pt-BR")} m${ativo.cota_origem === "relevo" ? " (pelo relevo)" : ""}`}</dd>
                <dt>Origem</dt><dd>{ORIGENS[ativo.origem] ?? ativo.origem} · versão {ativo.versao} · por {ativo.criado_por}</dd>
              </dl>
              {ativo.pendencias_ficha.length > 0 && <p className="nx-suave" style={{ margin: 0 }}>Falta na ficha técnica: {ativo.pendencias_ficha.join(", ")}.{" "}
                <button type="button" className="tl-fantasma" onClick={() => setAba("ficha")}>Completar</button></p>}
              <div className="tl-linha-acoes">
                {d.device_id && <Link className="nx-botao" to={`/cco/equipamento/${encodeURIComponent(d.device_id)}`}>Ver no CCO</Link>}
                <Link className="nx-botao" to={`/rede?foco=${encodeURIComponent(d.identificacao)}`}>Ver na Rede</Link>
              </div>
            </div>
          )}
          {aba === "dados" && (
            <>
              <div className="tl-painel-aba">
                <GrupoCampos titulo="Identificação">
                  {campo("nome", "Nome")}
                  <Campo id="dado-setor" rotulo="Setor"><CampoSetor empresa={ativo.empresa} id="dado-setor" valor={String(dados.setor ?? "")} desabilitado={!editavel}
                    aoMudar={(v) => setDados({ ...dados, setor: v })} /></Campo>
                  {campo("unidade", "Unidade")}
                  {campo("device_id", "Dispositivo de telemetria")}
                  {dados.tipo === "registro" && (
                    <Campo id="dado-fechado" rotulo="Estado normal do registro">
                      <select id="dado-fechado" disabled={!editavel} value={dados.registro_fechado === null ? "" : String(dados.registro_fechado)}
                        onChange={(e) => setDados({ ...dados, registro_fechado: e.target.value === "" ? null : e.target.value === "true" })}>
                        <option value="">—</option><option value="false">Aberto</option><option value="true">Fechado</option></select>
                    </Campo>
                  )}
                </GrupoCampos>
                <GrupoCampos titulo="Localização">
                  {campo("latitude", "Latitude", "number")}
                  {campo("longitude", "Longitude", "number")}
                  {campo("cota_m", `Cota${ativo.cota_origem ? ` · ${ativo.cota_origem === "relevo" ? "pelo relevo" : "informada"}` : ""}`, "number", "m")}
                  <div className="tl-campo largo"><span className="nx-suave" style={{ fontSize: 12 }}>Posição{editavel ? " · clique ou arraste o ponto" : ""}</span>
                    <MapaPosicao latitude={dados.latitude} longitude={dados.longitude} editavel={editavel}
                      aoMudar={(lat, lon) => setDados({ ...dados, latitude: Math.round(lat * 1e7) / 1e7, longitude: Math.round(lon * 1e7) / 1e7 })} /></div>
                </GrupoCampos>
              </div>
              {editavel && <RodapeFormulario sujo={mudou} valido={Object.keys(erros).length === 0} salvando={salvando}
                aoCancelar={() => setDados(ativo.dados)} aoSalvar={() => setDialogo("salvar")} />}
            </>
          )}
          {aba === "ficha" && (
            <div className="tl-painel-aba">
              {/* a ficha mínima também depende dos dados (dispositivo, registro): recarrega a cada nova versão do ativo */}
              <FichaForm key={`${ativo.versao}-${ativo.situacao}`} ativoId={ativo.id} tipo={d.tipo} editavel={editavel}
                aoSalvar={() => { void carregar(); avisar("Ficha técnica salva"); }} />
            </div>
          )}
          {aba === "telemetria" && (
            <div className="tl-painel-aba">
              {!d.device_id ? <EstadoVazio titulo="Sem telemetria" texto="Ligue um dispositivo na aba Dados."
                acao={editavel ? <button type="button" className="nx-botao" onClick={() => setAba("dados")}>Ir para os dados</button> : undefined} />
                : !leitura ? <EstadoVazio titulo="Sem leitura recente" texto={`O dispositivo ${d.device_id} não mandou leitura nas últimas horas.`} /> : (
                  <>
                    <small className="nx-suave" style={{ textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 11 }}>
                      {nomeMedida(leitura.medida)} · últimas 24 h ({unidadeMedida(leitura.medida)})</small>
                    {serie ? <Grafico24h pontos={serie.pontos} medida={serie.medida} /> : <Esqueleto linhas={4} />}
                    <dl className="tl-kv">{Object.values(tel?.leituras ?? {}).flatMap((l) => [
                      <dt key={`${l.medida}-r`}>{nomeMedida(l.medida)}</dt>,
                      <dd key={`${l.medida}-v`}>{fmtValor(l.medida, l.valor)} {unidadeMedida(l.medida)} · {idadeRelativa(l.timestamp)}</dd>])}</dl>
                  </>
                )}
            </div>
          )}
          {aba === "historico" && (
            <div className="tl-painel-aba">
              {eventos.length === 0 ? <EstadoVazio titulo="Sem histórico" /> : (
                <ol className="tl-linha-tempo">{eventos.map((e, i) => (
                  <li key={i}><b>{EVENTOS[e.evento] ?? e.evento}{e.tipo_autor === "agente" ? " · agente" : ""}</b>
                    <small>{fmtData(e.instante)} · {e.autor} · versão {e.versao}{e.motivo ? ` · “${e.motivo}”` : ""}</small>
                    {Object.keys(e.mudancas).length > 0 && <small style={{ display: "block" }}>{Object.entries(e.mudancas).map(([c, v]) =>
                      `${c}: ${Array.isArray(v) && v.length === 2 ? `${texto(v[0])} → ${texto(v[1])}` : texto(v)}`).join(" · ")}</small>}
                  </li>
                ))}</ol>
              )}
              <p className="nx-suave" style={{ margin: 0, fontSize: 12 }}>O histórico não se apaga: cada mudança é uma versão nova.</p>
            </div>
          )}
        </section>
      </div>
      <DialogoMotivo aberto={dialogo !== null} minimo={3}
        titulo={dialogo === "rejeitar" ? `Rejeitar ${d.identificacao}` : `Salvar os dados de ${d.identificacao}`}
        texto={dialogo === "rejeitar" ? "O ativo sai das listas de validados e fica no histórico com o motivo." : "Os dados viram uma versão nova; o motivo fica no histórico."}
        rotuloConfirmar={dialogo === "rejeitar" ? "Rejeitar" : "Salvar"} exemplo={dialogo === "rejeitar" ? "Ex.: duplicado do PCR-02" : "Ex.: dispositivo trocado em campo"}
        aoCancelar={() => setDialogo(null)}
        aoConfirmar={(motivo) => dialogo === "rejeitar"
          ? executar(() => api(`/v1/ativos/${id}/rejeitar`, { metodo: "POST", corpo: { motivo } }), `${d.identificacao} rejeitado`)
          : executar(() => api(`/v1/ativos/${id}`, { metodo: "PUT", corpo: { versao: ativo.versao, motivo, dados } }), `${d.identificacao} salvo · versão nova no histórico`)} />
    </section>
  );
}
